import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Toggle } from '../ui/Toggle';
import { TagInput } from '../ui/TagInput';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { NumberInput } from '../ui/NumberInput';
import { ViewOnSite } from './ViewOnSite';
import { useEditorItem } from './useEditorItem';
import { useAdmin } from '../AdminContext';
import { SeoPanel } from '../ui/SeoPanel';
import { CheckList } from '../ui/CheckList';
import { treatmentSeoChecks } from './catalogSeo';
import { treatmentSeoAuto } from '../../utils/seoAuto';
import { treatmentCategories } from '../../data/treatments';
import { KuurPriceNote } from './KuurPriceNote';
import { MediaField } from '../ui/MediaField';
import { emptyMedia, hasMedia } from '../../core/rules/media';
import { KUUR_DEFAULT_SESSIONS, KUUR_MIN_SESSIONS, TAG_SUGGESTIONS, asTreatmentCategory, emptyTreatment, uniqueSorted } from './catalogRules';
import { btnPrimary, inputCls } from '../ui/styles';
import { slugify, uniqueSlug } from '../editing';
import type { Treatment } from '../../core/types/catalog';
const back = {
  to: '/be/behandelingen',
  label: 'Alle behandelingen'
};
const EMPTY_MEDIA = emptyMedia();
type Kuur = NonNullable<Treatment['kuur']>;

/** Eén behandeling: tekst, duur, prijs, kuur en kenmerken. Het online consult wordt bij Instellingen beheerd. */
export function TreatmentEditor() {
  const {
    slug = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    catalog,
    site,
    putItem,
    removeItem
  } = useAdmin();
  // Het concept: een wijziging in Instellingen (consult, maximum sessies) werkt hier meteen door.
  const policy = site.policy;
  const {
    isNew,
    item: t,
    set
  } = useEditorItem('treatments', slug, emptyTreatment);
  if (!t) return <MissingItem title="Behandeling niet gevonden" back={back} data-id="element-916" data-mp="2ye04hndrnh5v" />;
  const isConsult = t.slug === policy.consult.slug;
  const linked = [...catalog.routines.filter(r => r.treatmentSlug === t.slug).map(r => `protocol ${r.name}`), ...catalog.concerns.filter(c => c.treatmentSlug === t.slug).map(c => `huidthema ${c.label}`)];
  const allFocus = uniqueSorted(catalog.treatments.map(x => x.focus));
  const kuur = t.kuur;
  const setKuur = (patch: Partial<Kuur>) => kuur && set({
    kuur: {
      ...kuur,
      ...patch
    }
  });
  const add = () => {
    const created = {
      ...t,
      slug: uniqueSlug(slugify(t.name), catalog.treatments.map(x => x.slug))
    };
    putItem('treatments', created);
    navigate(`/be/behandelingen/${created.slug}`, {
      replace: true
    });
  };
  return <div data-id="element-917" data-mp="18ypeviriemcj">
      <PageHeader title={isNew ? 'Nieuwe behandeling' : t.name} back={back} actions={isNew ? <button type="button" onClick={add} disabled={!t.name.trim()} className={btnPrimary} data-id="element-919" data-mp="2jf2ag3bx9cx">
              Behandeling toevoegen
            </button> : <>
              {!isConsult && <DeleteButton undoable blockedReason={linked.length ? `Kan nog niet: gekoppeld aan ${linked.join(', ')}. Koppel eerst los.` : undefined} onConfirm={() => {
        const restore = removeItem('treatments', t.slug);
        navigate(back.to);
        undoToast(`“${t.name || 'Naamloze behandeling'}” verwijderd`, restore, {
          description: 'Pas definitief als je opslaat.',
          restored: 'Behandeling teruggezet'
        });
      }} data-id="element-920" data-mp="3jin9p1hdlh9s" />}
              <ViewOnSite path="/behandelingen" data-id="element-921" data-mp="g52mxl7qrbvw" />
            </>} data-id="element-918" data-mp="18yz4v6ck0ti8" />

      {isConsult && <p className="mb-6 rounded-md border border-line bg-shell px-4 py-3 text-[13px] leading-relaxed text-muted" data-id="element-922" data-mp="l22vc2s2cqq6">
          Naam, duur en prijs van het online consult stel je in bij{' '}
          <Link to="/be/instellingen?tab=afspraken" className="text-ink underline underline-offset-2" data-id="element-923" data-mp="2mur1ajw3ds9n">
            Instellingen › Afspraken & kuren
          </Link>
          , omdat ze ook in de FAQ en de voorwaarden staan.
        </p>}

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-924" data-mp="q6d5z59mz6n3">
        <div className="space-y-6 lg:col-span-8" data-id="element-925" data-mp="q63flgkoixgv">
          <Panel title="Behandeling" data-id="element-926" data-mp="l7s6j827kqzp">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-927" data-mp="l8trq5cxutv5">
              <Field label="Naam" className="sm:col-span-2" data-id="element-928" data-mp="3tn2cwsd4sida">
                <input value={t.name} onChange={e => set({
                name: e.target.value
              })} disabled={isConsult} className={inputCls} data-id="element-929" data-mp="3tnlkpg92hqdl" />
              </Field>
              <Field label="Korte zin" hint="Onder de naam in de lijst." className="sm:col-span-2" data-id="element-930" data-mp="78ajax4k9lsg">
                <input value={t.tagline} onChange={e => set({
                tagline: e.target.value
              })} className={inputCls} data-id="element-931" data-mp="2gsl2gtx9zb6" />
              </Field>
              <Field label="Omschrijving" className="sm:col-span-2" data-id="element-932" data-mp="3av9hysei4n2x">
                <textarea value={t.description} onChange={e => set({
                description: e.target.value
              })} rows={4} className={`${inputCls} resize-y leading-relaxed`} data-id="element-933" data-mp="35yj2o7aitvik" />
              </Field>
              <Field label="Categorie" data-id="element-934" data-mp="1q6yuq8k4v2jh">
                <select value={t.category} onChange={e => {
                const category = asTreatmentCategory(e.target.value);
                if (category) set({
                  category
                });
              }} className={inputCls} data-id="element-935" data-mp="1q807chgsrx3b">
                  {treatmentCategories.map(c => <option key={c} data-id="element-936" data-mp="295t5z7ke1kuw">{c}</option>)}
                </select>
              </Field>
              <Field label="Duur (minuten)" hint="Bepaalt ook de tijdsloten in de agenda." data-id="element-937" data-mp="1v6bgsr36k5l0">
                <NumberInput value={t.durationMinutes || undefined} onChange={v => set({
                durationMinutes: v ?? 0
              })} min={15} max={480} emptyMessage="Vul de duur in minuten in." disabled={isConsult} data-id="element-938" data-mp="203tqwksxoqul" />
              </Field>
              <Field label="Waar het om draait" as="div" className="sm:col-span-2" data-id="element-939" data-mp="4pfwcekzulsv">
                <TagInput label="Waar het om draait" value={t.focus} onChange={focus => set({
                focus
              })} suggestions={allFocus.filter(f => !t.focus.includes(f)).slice(0, TAG_SUGGESTIONS)} data-id="element-940" data-mp="m9qv95pgyng" />
              </Field>
            </div>
          </Panel>

          <Panel title="Prijs" data-id="element-941" data-mp="3i1zpt7vu2cr4">
            <div className="space-y-5" data-id="element-942" data-mp="3i315jpnsnnf6">
              <Toggle checked={t.price === null} onChange={onlyKuur => set({
              price: onlyKuur ? null : 0
            })} label="Alleen als kuur te boeken" description="Dan staat er “Alleen als kuur” in plaats van een losse prijs." data-id="element-943" data-mp="3hzvggaf60ljg" />
              {t.price !== null && <Field label="Prijs per behandeling (€)" data-id="element-944" data-mp="17aghjprvsl9m">
                  <NumberInput decimals value={t.price || undefined} onChange={v => set({
                price: v ?? 0
              })} emptyMessage="Vul een prijs in, bijv. 95." disabled={isConsult} className="max-w-[12rem]" data-id="element-945" data-mp="177u7njhipto8" />
                </Field>}
              <Toggle checked={Boolean(kuur)} onChange={on => set({
              kuur: on ? {
                sessions: KUUR_DEFAULT_SESSIONS,
                price: 0
              } : undefined
            })} label="Ook als kuur aanbieden" data-id="element-946" data-mp="1jl7py1iuoah0" />
              {kuur && <div className="grid max-w-md gap-4 sm:grid-cols-2" data-id="element-947" data-mp="vu2332ehshx7">
                  <Field label="Aantal behandelingen" data-id="element-948" data-mp="10u6i0nzr54lb">
                    <NumberInput value={kuur.sessions} onChange={v => v !== undefined && setKuur({
                  sessions: v
                })} min={KUUR_MIN_SESSIONS} max={policy.courseMaxSessions} data-id="element-949" data-mp="10sdc5awyqoed" />
                  </Field>
                  <Field label="Prijs kuur (€)" data-id="element-950" data-mp="2g8vmmujqz07j">
                    <NumberInput decimals value={kuur.price || undefined} onChange={v => setKuur({
                  price: v ?? 0
                })} emptyMessage="Vul de kuurprijs in." data-id="element-951" data-mp="32bpedn6f2ngn" />
                  </Field>
                  <KuurPriceNote sessions={kuur.sessions} kuurPrice={kuur.price} singlePrice={t.price} data-id="element-952" data-mp="3c21y5d6eqclq" />
                </div>}
            </div>
          </Panel>

          {!isConsult && <Panel title="Beeld" description="Staat groot naast de uitleg als iemand de behandeling openklapt op de salonpagina." data-id="element-953" data-mp="3l2gkmfv3xxqt">
              <MediaField label="Foto of korte video" value={t.media ?? EMPTY_MEDIA}
          // Helemaal leeg = veld weg (zelfde opslag als vóór beelden); alleen tekst blijft staan tijdens het typen.
          onChange={media => set({
            media: media.alt || media.video || hasMedia(media) ? media : undefined
          })} emptyNote="Leeg: de behandeling staat er zonder beeld, alleen met tekst." data-id="element-954" data-mp="3g2luwfn2je7c" />
            </Panel>}

          <SeoPanel seo={t.seo} onChange={seo => set({
          seo
        })} auto={treatmentSeoAuto(t)} path={null} imageFallback="de algemene salonfoto" data-id="element-955" data-mp="b6st8mydpziq" />
        </div>

        <aside aria-label="Behandelinstellingen" className="space-y-6 lg:col-span-4" data-id="element-956" data-mp="1fy5tf9ectvp">
          <Panel title="Kenmerken" data-id="element-957" data-mp="1e3u8rk0me4a">
            <div className="space-y-1" data-id="element-958" data-mp="6ehuvojaelsa">
              <Toggle checked={Boolean(t.signature)} onChange={signature => set({
              signature
            })} label="Signature-behandeling" description="Krijgt een label op de salonpagina." data-id="element-959" data-mp="6d6p5j6a73yj" />
              <Toggle checked={Boolean(t.bindweefsel)} onChange={bindweefsel => set({
              bindweefsel
            })} label="Met bindweefselmassage" data-id="element-960" data-mp="3en9mwh1ua3x7" />
              <Toggle checked={Boolean(t.online)} onChange={online => set({
              online
            })} label="Online (videocall)" data-id="element-961" data-mp="3jm2yu51h9mv9" />
            </div>
          </Panel>
          <Panel title="Wat mist er nog?" data-id="element-962" data-mp="1hupy7s6jm150">
            <CheckList checks={treatmentSeoChecks(t, catalog.treatments)} okText="Deze behandeling is compleet" data-id="element-963" data-mp="1htocmtrwhw91" />
          </Panel>
          {linked.length > 0 && <Panel title="Gekoppeld aan" data-id="element-964" data-mp="26dv4fngx7k0m">
              <ul className="space-y-1 text-[13px] text-muted" data-id="element-965" data-mp="26ewky7gpgypq">
                {linked.map(l => <li key={l} data-id="element-966" data-mp="1sk3w2iexe66h">{l}</li>)}
              </ul>
            </Panel>}
        </aside>
      </div>
    </div>;
}